const VIDEO_SERVICES = {
    YOUTUBE: 'youtube',
    VIMEO: 'vimeo',
    DAILYMOTION: 'dailymotion',
    WISTIA: 'wistia'
};

export class VideoThumbnailService {
    static #extractYouTubeId = (url) => {
        const patterns = {
            'youtube.com/watch': url => new URL(url).searchParams.get('v'),
            'youtu.be/': url => url.split('youtu.be/')[1]?.split('?')[0],
            'youtube.com/embed/': url => url.split('embed/')[1]?.split('?')[0],
            'youtube.com/shorts/': url => url.split('shorts/')[1]?.split('?')[0]
        };

        const matchingPattern = Object.entries(patterns)
            .find(([pattern]) => url.includes(pattern));

        return matchingPattern ? matchingPattern[1](url) : null;
    };

    static #extractVimeoId = (url) => {
        const regex = /vimeo\.com\/(\d+)(?:\/[^\/]*)?$/;

        const match = url.match(regex);

        return match && match[1] ? match[1] : null;
    };

    static #extractDailymotionId = (url) => {
        const regex = /dailymotion\.com\/(?:video|embed\/video)\/([a-zA-Z0-9]+)(?:\/[^\/]*)?/;

        const match = url.match(regex);

        return match && match[1] ? match[1] : null;
    };

    static #generateYouTubeThumbnails = (videoId) => [
        { url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`, width: 1280, height: 720 },
        { url: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`, width: 640, height: 480 },
        { url: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`, width: 480, height: 360 },
        { url: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`, width: 320, height: 180 },
        { url: `https://img.youtube.com/vi/${videoId}/default.jpg`, width: 120, height: 90 }
    ];

    static #fetchVimeoThumbnails = async (videoId) => {
        const response = await fetch(`https://vimeo.com/api/v2/video/${videoId}.json`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const [data] = await response.json();
        return [
            { url: data.thumbnail_large, width: 640, height: 360 },
            { url: data.thumbnail_medium, width: 200, height: 150 },
            { url: data.thumbnail_small, width: 100, height: 75 }
        ];
    };

    static #extractWistiaId = (url) => {
        if (url.includes('/medias/')) {
            return url.split('/medias/')[1];
        } else if (url.includes('embed/iframe/')) {
            return url.split('embed/iframe/')[1];
        }
        return new URL(url).searchParams.get('wmediaid');
    };

    static #fetchWistiaThumbnails = async (videoId) => {
        const apiUrl = `https://fast.wistia.com/oembed?url=https://home.wistia.com/medias/${videoId}`;
        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        return [{
            url: data.thumbnail_url,
            width: data.thumbnail_width,
            height: data.thumbnail_height
        }];
    };

    static async getThumbnails(videoUrl) {
        if (!videoUrl) {
            throw new Error('Video URL is required');
        }

        const serviceHandlers = {
            [VIDEO_SERVICES.YOUTUBE]: async (url) => {
                const videoId = this.#extractYouTubeId(url);
                if (!videoId) throw new Error('Invalid YouTube URL format');
                return this.#generateYouTubeThumbnails(videoId);
            },
            [VIDEO_SERVICES.VIMEO]: async (url) => {
                const videoId = this.#extractVimeoId(url);
                return await this.#fetchVimeoThumbnails(videoId);
            },
            [VIDEO_SERVICES.DAILYMOTION]: (url) => {
                const videoId = this.#extractDailymotionId(url);
                return [{ url: `https://www.dailymotion.com/thumbnail/video/${videoId}`, width: 800, height: 450 }];
            },
            [VIDEO_SERVICES.WISTIA]: async (url) => {
                const videoId = this.#extractWistiaId(url);
                if (!videoId) throw new Error('Invalid Wistia URL format');
                return await this.#fetchWistiaThumbnails(videoId);
            }
        };

        const service = Object.entries(VIDEO_SERVICES)
            .find(([, name]) => {
                const domain = name.toLowerCase();
                return videoUrl.includes(`${domain}.com`) || videoUrl.includes(`${domain}.net`);
            })?.[1];

        if (!service || !serviceHandlers[service]) {
            throw new Error('Unsupported video service');
        }

        const thumbnails = await serviceHandlers[service](videoUrl);
        return {
            service,
            thumbnails: thumbnails.map(({ url, width, height }) => ({
                src: url,
                width,
                height
            }))
        };
    }
}

export const hasInnerBlocksContent = (_innerBlocks) => {
    if (!_innerBlocks) {
        return false;
    }

    for (const innerBlock of _innerBlocks) {
        if (innerBlock.attributes.content && innerBlock.attributes.content.trim().length > 0) {
            return true;
        }

        if (innerBlock.innerBlocks && innerBlock.innerBlocks.length > 0) {
            if (hasInnerBlocksContent(innerBlock.innerBlocks)) {
                return true;
            }
        }

        if (innerBlock.name === 'core/details' && innerBlock.attributes.summary.trim().length > 0) {
            return true;
        }
    }
    return false;
};

export const buildCreditProperties = ({
                                          hasCreditOverlap,
                                          backgroundColor,
                                          style,
                                          blockProps
                                      }) => {
    const baseCreditClass = 'simple-video-preview__credit';
    const overlapClass = hasCreditOverlap ? 'overlapping' : '';
    const creditClassName = [baseCreditClass, overlapClass]
        .filter(Boolean)
        .join(' ');

    const baseInnerClass = 'simple-video-preview__credit-inner';
    const backgroundClass = backgroundColor
        ? `has-${backgroundColor}-background-color has-background`
        : '';
    const creditInnerClassName = [baseInnerClass, backgroundClass]
        .filter(Boolean)
        .join(' ');

    const creditInnerStyle = {
        ...blockProps.style,
        backgroundColor: backgroundColor
            ? undefined
            : style?.color?.background
    };

    return {
        creditClassName,
        creditInnerClassName,
        creditInnerStyle
    };
};

export const getVideoThumbnails = async (videoUrl) => {
    try {
        return await VideoThumbnailService.getThumbnails(videoUrl);
    } catch (error) {
        console.error(error.message);
        return null;
    }
};
